import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Camera, Megaphone, Monitor, Compass } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSiteContent } from "../context/SiteContentContext";
import { serviceMenu } from "../i18n/services";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

const iconMap = {
  "social-media-management": Camera,
  "ads": Megaphone,
  "website": Monitor,
  "consultanta": Compass,
};

export default function ServicesPage() {
  const { t, lang } = useLanguage();
  const { mergedServicePages } = useSiteContent();
  const menu = serviceMenu[lang] || serviceMenu.ro;
  const pages = mergedServicePages[lang === "en" ? "en" : "ro"] || mergedServicePages.ro;

  return (
    <main className="bg-white" data-testid="services-page">
      {/* Header */}
      <section className="relative overflow-hidden bg-[#3e2723] pt-40 pb-24 sm:pt-52 sm:pb-32">
        <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-[#fbd9e5]/15 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute bottom-0 -left-40 h-[400px] w-[400px] rounded-full bg-[#fbd9e5]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#fbd9e5]/70"
          >
            {t.services_page.eyebrow}
          </motion.p>
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className="mt-6 font-display text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] text-[#fbd9e5] max-w-5xl"
          >
            {t.services_page.title}
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className="mt-8 max-w-2xl text-lg text-[#fbd9e5]/80 leading-relaxed"
          >
            {t.services_page.subtitle}
          </motion.p>
        </div>
      </section>

      {/* 4 service tiles */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="grid gap-6 md:grid-cols-2">
            {menu.map((s, i) => {
              const Icon = iconMap[s.slug] || Camera;
              const page = pages[s.slug];
              return (
                <motion.div
                  key={s.slug}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                >
                  <Link
                    to={`/services/${s.slug}`}
                    className="group block relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] bg-[#fbd9e5] p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1 h-full"
                    data-testid={`service-tile-${s.slug}`}
                  >
                    <div
                      aria-hidden
                      className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-white/50 blur-3xl opacity-70 group-hover:opacity-100 transition-opacity"
                    />
                    <div className="relative flex flex-col h-full">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/70 border border-[#3e2723]/10 text-[#3e2723] group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5] group-hover:rotate-45 transition-all duration-300">
                          <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>
                      <h2 className="mt-8 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#3e2723] leading-tight">
                        {s.label}
                      </h2>
                      <p className="mt-3 text-[15px] leading-relaxed text-[#3e2723]/75 max-w-md">
                        {s.desc}
                      </p>
                      {page?.lead ? (
                        <p className="mt-6 text-[13.5px] leading-relaxed text-[#3e2723]/60">
                          {page.lead}
                        </p>
                      ) : null}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#faf8f8] py-24 sm:py-32">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter text-[#3e2723] leading-[1.05]">
            {t.services_page.cta_title}
          </h2>
          <p className="mt-6 text-lg text-[#7a5c55]">{t.services_page.cta_subtitle}</p>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#3e2723] text-[#fbd9e5] px-8 py-4 text-[15px] font-semibold hover:-translate-y-1 hover:bg-[#2a1a17] transition-all duration-300 shadow-lg"
            data-testid="services-bottom-cta"
          >
            {t.services_page.cta_button}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
